import {isCpfOuCnpj, isEmail} from "../../shared/utils/validators";
import {onlyDigits} from "../../shared/utils/formatters";
import {getStatusLabel} from "../Licencas/licencasUtils";

// Os dois dias vão de 1 a 28, como o banco e o app-empresas (BBF-137/148): todo mês tem esses dias.
export const DIAS_DO_CICLO = Array.from({length: 28}, (_, index) => index + 1);

export const MEIO_LABELS = {BOLETO: 'Boleto com Pix', CARTAO: 'Cartão'};
export const getMeioLabel = (meio) => MEIO_LABELS[meio] || meio || '-';

/*
 * O que os dois dias querem dizer, como o faturamento calcula: a fatura sai no dia do fechamento, e vence no
 * primeiro dia de vencimento depois dela. Vencimento igual ou antes do fechamento cai no mês seguinte.
 */
export const explicarCiclo = (fechamento, vencimento) => {
    const f = Number(fechamento);
    const v = Number(vencimento);
    if (!DIAS_DO_CICLO.includes(f) || !DIAS_DO_CICLO.includes(v)) return 'Os dias de fechamento e de vencimento vão de 1 a 28.';
    if (v > f) {
        const prazo = v - f;
        return `A fatura sai no dia ${f} e vence no dia ${v} do mesmo mês: ${prazo} ${prazo === 1 ? 'dia' : 'dias'} para pagar.`;
    }
    return `A fatura sai no dia ${f} e vence no dia ${v} do mês seguinte.`;
};

const CAMPOS_DO_ENDERECO = ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'];

// Endereço sem nenhum campo preenchido: o bloco que o formulário mostra para a empresa que não tem endereço.
export const enderecoVazio = (endereco) => !CAMPOS_DO_ENDERECO.some((campo) => String(endereco?.[campo] ?? '').trim());

/*
 * Erros do cadastro, na ordem da tela. As regras são as do app-empresas, para o erro aparecer antes de ir ao
 * backend: documento com dígito verificador, e-mail, telefone com DDD, dias de 1 a 28 e endereço principal.
 *
 * O app-empresas aceita empresa sem endereço; a tela exige o principal no cadastro (o boleto precisa do endereço
 * do pagador). `enderecoOpcional` é para editar a empresa que nunca teve endereço, como a criada pela API: o bloco
 * vazio não trava o salvamento, e quem começa a preenchê-lo volta a ter os quatro campos exigidos.
 */
export const validarEmpresa = (empresa, enderecoPrincipal, {enderecoOpcional = false} = {}) => {
    const errors = {};
    if (!empresa.razao_social || empresa.razao_social.trim().length < 2) errors.razao_social = 'Informe a razão social';
    if (!isCpfOuCnpj(empresa.cnpj)) {
        errors.cnpj = 'CPF ou CNPJ inválido: confira os dígitos verificadores';
    }
    if (!empresa.email?.trim()) errors.email = 'Informe o e-mail: é para onde vão a fatura e a nota';
    else if (!isEmail(empresa.email.trim())) errors.email = 'E-mail inválido';

    const telefone = onlyDigits(empresa.telefone);
    if (telefone && telefone.length < 10) errors.telefone = 'Informe o DDD do telefone';
    if (telefone.length > 11) errors.telefone = 'Telefone deve ter no máximo 11 dígitos';

    if (!DIAS_DO_CICLO.includes(Number(empresa.dia_fechamento)) || !DIAS_DO_CICLO.includes(Number(empresa.dia_vencimento))) {
        errors.faturamento = 'Os dias de fechamento e de vencimento vão de 1 a 28';
    }

    if (enderecoOpcional && enderecoVazio(enderecoPrincipal)) return errors;
    if (!enderecoPrincipal.logradouro || enderecoPrincipal.logradouro.trim().length < 2) errors.logradouro = 'Informe o logradouro';
    if (!enderecoPrincipal.cidade || enderecoPrincipal.cidade.trim().length < 2) errors.cidade = 'Informe a cidade';
    if (!enderecoPrincipal.uf || enderecoPrincipal.uf.trim().length !== 2) errors.uf = 'UF inválida';
    if (!enderecoPrincipal.cep || enderecoPrincipal.cep.replace(/\D/g, '').length !== 8) errors.cep = 'CEP inválido';
    return errors;
};

// Nome de uma assinatura nas listas: plano, versão e tenant (a assinatura é o tenant; não tem nome próprio).
export const rotuloDaLicenca = (licenca) => {
    const plano = licenca?.versao?.nome_plano || licenca?.plano?.nome_plano || 'Plano';
    const versao = licenca?.versao?.numero ? ` v${licenca.versao.numero}` : '';
    return `${plano}${versao}`;
};

// O que está agendado na assinatura, em uma frase curta para a lista.
export const agendadosDaLicenca = (licenca) => {
    const itens = [];
    if (licenca?.cancelamento?.agendado_para) itens.push('cancelamento agendado');
    if (licenca?.troca_agendada?.vale_a_partir_de) itens.push('troca de plano agendada');
    return itens;
};

export const situacaoDaLicenca = (licenca) => getStatusLabel(licenca?.status);
